import { LineupPlayer } from '@/lib/supabase-types';

// Parse a SportMonks formation string ("4-3-3", "4-3-1-2") into row counts,
// prepending a single goalkeeper row. Returns [] for null/garbage input.
export function parseFormationRows(formation: string | null): number[] {
  if (!formation) return [];
  const parts = formation
    .split(/[-–]/)
    .map(s => parseInt(s.trim(), 10))
    .filter(n => Number.isFinite(n) && n > 0);
  return parts.length > 0 ? [1, ...parts] : [];
}

/** SportMonks position ids → short labels for admin/fan display. */
export const POSITION_LABELS: Record<number, string> = {
  24: 'GK',
  25: 'DEF',
  26: 'MID',
  27: 'ATT',
};

/** Position choices for the admin editor, in pitch order (SportMonks ids). */
export const POSITION_OPTIONS: { id: number; label: string }[] = [
  { id: 24, label: 'GK' },
  { id: 25, label: 'DEF' },
  { id: 26, label: 'MID' },
  { id: 27, label: 'ATT' },
];

/**
 * Normalize a club-edited lineup before saving so it always counts as REAL
 * for the fan-facing gate:
 *  - seed formation_position 1..11 where missing (placeholder rows have none);
 *  - drop position_id only when EVERY player is stamped GK (24) — the exact
 *    SportMonks placeholder shape, which would badge everyone "GK". Any other
 *    tagging is a deliberate club edit and keeps its tags: partial (e.g. only
 *    the goalkeeper marked) or even uniformly non-GK.
 */
export function normalizeLineup(lineup: LineupPlayer[]): LineupPlayer[] {
  const allTagged = lineup.every(p => p.position_id !== null && p.position_id !== undefined);
  const distinctPos = new Set(
    lineup.map(p => p.position_id).filter(v => v !== null && v !== undefined),
  );
  const junkPositions = lineup.length > 1 && allTagged && distinctPos.size === 1 && distinctPos.has(24);
  return lineup.map((p, i) => ({
    ...p,
    formation_position: p.formation_position ?? i + 1,
    position_id: junkPositions ? null : p.position_id,
  }));
}

/**
 * Is this a REAL lineup worth showing to fans — as opposed to SportMonks'
 * pre-announcement placeholder squad?
 *
 * The placeholder shape (observed live, Aug 2026): `formation` null, every
 * player stamped `position_id = 24` (GK), `formation_position` null. Rendering
 * it produces a nonsense pitch, so fan surfaces should show their "lineup
 * available closer to kickoff" empty state instead.
 *
 * Accepts (must NOT be hidden):
 *  - a parseable formation string (real SportMonks lineup), or
 *  - >1 distinct non-null formation_position — club-entered lineups from the
 *    manual LineupEditor seed positions 1..11 with formation null, or
 *  - >1 distinct non-null position_id — real per-player positions even when
 *    the formation string is missing.
 */
export function hasRealLineupData(
  lineup: { formation: string | null; players_json: LineupPlayer[] | null } | null | undefined,
): boolean {
  if (!lineup) return false;
  const players = lineup.players_json ?? [];
  if (players.length === 0) return false;

  if (parseFormationRows(lineup.formation).length > 0) return true;

  const formationPositions = new Set(
    players.map(p => p.formation_position).filter((v): v is number => v !== null && v !== undefined),
  );
  if (formationPositions.size > 1) return true;

  const positionIds = new Set(
    players.map(p => p.position_id).filter((v): v is number => v !== null && v !== undefined),
  );
  return positionIds.size > 1;
}
